
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ThemeProvider } from './services/ThemeContext';
import { initErrorTracker } from './services/errorTracker';

// Initialize i18n before rendering (sets <html lang="..."> and loads translations)
import './services/i18n';

import { purgeServiceWorkerCaches, forceHardReload } from './services/lazyWithRetry';

// Initialize error tracking before anything renders
initErrorTracker();

// Global handler for Vite dynamic import preload failures
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', async (event) => {
    console.warn('[Scrudix] Vite preload error detected, auto-recovering:', event);
    event.preventDefault();
    const reloadKey = 'scrudix:chunk-reload-once';
    const lastReloadStr = sessionStorage.getItem(reloadKey);
    const now = Date.now();
    if (!lastReloadStr || now - parseInt(lastReloadStr, 10) > 8000) {
      sessionStorage.setItem(reloadKey, now.toString());
      await purgeServiceWorkerCaches();
      forceHardReload();
    }
  });
}

// Load server-injected Google Maps embed key at runtime (avoids Vite bundling issues)
try {
  fetch('/api/maps/embed-key.js')
    .then((res) => res.text())
    .then((js) => {
      const script = document.createElement('script');
      script.textContent = js;
      document.head.appendChild(script);
    })
    .catch(() => {});
} catch {
  // ignore
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// ─── Register Service Worker for PWA ──
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js?v=1.2.0').then(
      (reg) => {
        console.log('[SW] Registered:', reg.scope);
        // Force immediate activation
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content available, reload to activate
                window.location.reload();
              }
            });
          }
        });
      },
      (err) => console.warn('[SW] Registration failed:', err)
    );
  });
}
